// src/settingComponent/apiSetting/ApiKeySection.jsx
import React, { useState } from 'react';
import './apiKeySection.css';
import './apikeyPopups/ApiPopupAdd.css';
import './apikeyPopups/ApiPopupEdit.css';
import './apikeyPopups/ApiPopupDelete.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faCopy, faRocket } from '@fortawesome/free-solid-svg-icons';
import ApiPopupAdd from './apikeyPopups/ApiPopupAdd';
import ApiPopupEdit from './apikeyPopups/ApiPopupEdit';
import ApiPopupDelete from './apikeyPopups/ApiPopupDelete';

const ApiKeySection = ({
  isApiEnabled,
  isEnabling,
  onEnableAPI,
  error,
  products = [],
  apiKeys = []
}) => {
  const [showAddPopup, setShowAddPopup]     = useState(false);
  const [editingKey, setEditingKey]         = useState(null);
  const [deletingKey, setDeletingKey]       = useState(null);
  const [copiedKeyId, setCopiedKeyId]       = useState(null);

  const openAdd    = () => setShowAddPopup(true);
  const closeAdd   = () => setShowAddPopup(false);
  const openEdit   = key => setEditingKey(key);
  const closeEdit  = () => setEditingKey(null);
  const openDelete = key => setDeletingKey(key);
  const closeDelete= () => setDeletingKey(null);

  const maskKey = key => {
    const head = key.slice(0, 8);
    const tail = key.slice(-4);
    return `${head}…${tail}`;
  };

  const formatDate = ts => {
    let d;
    if (ts && typeof ts.toDate === 'function') d = ts.toDate();
    else d = new Date(ts);
    return d.toLocaleDateString('en-US', {
      month: 'long', day: 'numeric', year: 'numeric'
    });
  };

  const renderPermissions = list => {
    if (list.length === 0) return 'Loading...';
    if (list.length === products.length) return 'All';
    return 'Restricted'; // When not all permissions are granted
  };

  const handleCopy = (key, id) => {
    navigator.clipboard.writeText(key);
    setCopiedKeyId(id);
    setTimeout(() => setCopiedKeyId(null), 1000);
  };

  // Check if the user has the maximum allowed keys (in this case, 9)
  const isMaxKeys = apiKeys.length >= 9;

  return (
    <div className="api-settings-container">

      {!isApiEnabled && (
        <div className="api-banner">
          <FontAwesomeIcon icon={faRocket} className="api-banner-icon" />
          <span className="api-banner-text">
            Get started with <strong>$5.00</strong> of free Bumpups API credit!
          </span>
        </div>
      )}

      <div className="api-header">
        <h1 className="api-title">API keys</h1>
        {isApiEnabled ? (
          <button
            className={`api-enable-button ${isMaxKeys ? 'max-keys' : ''}`}
            onClick={openAdd}
            disabled={isMaxKeys}
          >
            {isMaxKeys ? 'Max API Keys' : '+ Create API Key'}
          </button>
        ) : (
          <button
            className="api-enable-button"
            onClick={onEnableAPI}
            disabled={isEnabling}
          >
            {isEnabling ? 'Enabling...' : 'Enable API'}
          </button>
        )}
      </div>

      <div className="api-description">
        <p>
          As an owner of this account, you can view and manage all API keys here.
        </p>
        <p>
          Do not share your API key with others or expose it in the browser or other client-side code. To protect your account’s security, Bumpups may automatically disable any API key that has leaked publicly.
        </p>
        <p>
          Explore our&nbsp;
          <a
            href="https://docs.bumpups.com/docs/getting-started"
            target="_blank"
            rel="noopener noreferrer"
            className="api-link"
          >
            API docs
          </a>
          &nbsp;for step-by-step instructions on using the Bumpups API.
        </p>
      </div>

      {error && <p className="api-error-text">{error}</p>}

      <div className="api-table-container">
        <table className="api-table">
          <thead className={!isApiEnabled ? 'disabled' : ''}>
            <tr>
              <th>Name</th>
              <th>Secret key</th>
              <th>Created</th>
              <th>Permissions</th>
              <th className="api-actions-header"></th>
            </tr>
          </thead>
          {isApiEnabled && apiKeys.length > 0 && (
            <tbody>
              {apiKeys.map(key => (
                <tr key={key.id}>
                  <td>{key.apiKeyName || 'Secret key'}</td>
                  <td className="api-secret-cell">
                    {maskKey(key.apiKey)}
                    <button
                      className={`icon-button copy-button ${
                        copiedKeyId === key.id ? 'copied' : ''
                      }`}
                      onClick={() => handleCopy(key.apiKey, key.id)}
                      title="Copy secret key"
                    >
                      <FontAwesomeIcon icon={faCopy} className="api-icon" />
                    </button>
                  </td>
                  <td className="api-created-cell">
                    {key.apiKeyCreatedDatetime &&
                    typeof key.apiKeyCreatedDatetime.toDate === 'function'
                      ? formatDate(key.apiKeyCreatedDatetime)
                      : 'Loading...'}
                  </td>
                  <td className="api-permissions-cell">
                    {products.length === 0
                      ? 'Loading...'
                      : renderPermissions(key.apiKeyProductsList || [])}
                  </td>
                  <td>
                    <div className="api-action-icons">
                      <button
                        className="icon-button"
                        onClick={() => openEdit(key)}
                        title="Edit key"
                      >
                        <FontAwesomeIcon icon={faEdit} className="api-icon" />
                      </button>
                      <button
                        className="icon-button delete-button"
                        onClick={() => openDelete(key)}
                        title="Revoke key"
                      >
                        <FontAwesomeIcon icon={faTrash} className="api-icon" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          )}
        </table>

        {!isApiEnabled && (
          <div className="no-keys-container">
            <p className="no-keys-message">
              You haven’t enabled the API yet.
            </p>
            <p className="no-keys-subtext">
              Click “Enable API” above to turn on API functionality.
            </p>
          </div>
        )}

        {isApiEnabled && apiKeys.length === 0 && (
          <div className="no-keys-container">
            <p className="no-keys-message">
              You haven’t created any API keys yet.
            </p>
            <p className="no-keys-subtext">
              Click “+ Create API Key” above to generate your first key.
            </p>
          </div>
        )}
      </div>

      {showAddPopup && (
        <ApiPopupAdd
          products={products}
          onClose={closeAdd}
          onCreate={closeAdd}
        />
      )}
      {editingKey && (
        <ApiPopupEdit
          products={products}
          keyData={editingKey}
          onClose={closeEdit}
          onSave={closeEdit}
        />
      )}
      {deletingKey && (
        <ApiPopupDelete
          keyData={deletingKey}
          onClose={closeDelete}
          onDelete={closeDelete}
        />
      )}
    </div>
  );
};

export default ApiKeySection;
